/**
 * Production type registry
 *
 * Central definition of every production type the app supports (calf rearing,
 * fattening cattle / feedlot). All type-dependent behaviour — lifecycle stages,
 * disease sets, diagnoses, scoring systems — is resolved through this registry
 * instead of hardcoding calf assumptions.
 *
 * Adding a new production type = adding one entry here plus its ERP settings
 * defaults in calfERPSettingsService (and i18n labels).
 */

import {
	CALF_LIFECYCLE_STAGES,
	CALF_LIFECYCLE_STAGE_ORDER,
	CALF_DISEASE_CATEGORIES,
	CALF_DIAGNOSE_VALUES,
} from "./calfEnums";

export const PRODUCTION_TYPES = {
	CALF: "calf",
	FATTENING: "fattening",
};

export const DEFAULT_PRODUCTION_TYPE = PRODUCTION_TYPES.CALF;

/** Lifecycle stages for fattening cattle (feedlot flow: stocking-in → finish) */
export const FATTENING_LIFECYCLE_STAGES = {
	ARRIVAL: "arrival",
	GROWING: "growing",
	FINISHING: "finishing",
};

export const FATTENING_LIFECYCLE_STAGE_ORDER = [
	FATTENING_LIFECYCLE_STAGES.ARRIVAL,
	FATTENING_LIFECYCLE_STAGES.GROWING,
	FATTENING_LIFECYCLE_STAGES.FINISHING,
];

/** Disease categories relevant in the fattening phase */
export const FATTENING_DISEASE_CATEGORIES = {
	BRD: "brd",
	PNEUMONIA: "pneumonia",
	BLOAT: "bloat",
	LAMENESS: "lameness",
	ACIDOSIS: "acidosis",
	OTHER: "other",
};

export const FATTENING_DIAGNOSE_VALUES = [
	"brd",
	"pneumonia",
	"bloat",
	"lameness",
	"foot_rot",
	"digital_dermatitis",
	"acidosis",
	"dehydration",
	"fracture",
	"hernia",
	"other",
];

const PRODUCTION_TYPE_CONFIG = {
	unclassified: {
		key: 'unclassified', labelKey: 'cattle-unclassified', lifecycleStages: {}, lifecycleStageOrder: [],
		defaultEntryStage: '', diseaseCategories: {OTHER: 'other'}, diagnoseValues: ['other'],
		scoringSystems: {wisconsin: false, faecal: false}, settingsSection: 'root',
	},
	[PRODUCTION_TYPES.CALF]: {
		key: PRODUCTION_TYPES.CALF,
		labelKey: "production-type-calf",
		lifecycleStages: CALF_LIFECYCLE_STAGES,
		lifecycleStageOrder: CALF_LIFECYCLE_STAGE_ORDER,
		defaultEntryStage: CALF_LIFECYCLE_STAGES.BIRTH_ARRIVAL,
		diseaseCategories: CALF_DISEASE_CATEGORIES,
		diagnoseValues: CALF_DIAGNOSE_VALUES,
		// Wisconsin + faecal scoring only make sense for pre-weaning calves
		scoringSystems: { wisconsin: true, faecal: true },
		// Section of the ERP settings document holding this type's config.
		// "root" = top level of the config doc (calf settings predate sections).
		settingsSection: "root",
	},
	[PRODUCTION_TYPES.FATTENING]: {
		key: PRODUCTION_TYPES.FATTENING,
		labelKey: "production-type-fattening",
		lifecycleStages: FATTENING_LIFECYCLE_STAGES,
		lifecycleStageOrder: FATTENING_LIFECYCLE_STAGE_ORDER,
		defaultEntryStage: FATTENING_LIFECYCLE_STAGES.ARRIVAL,
		diseaseCategories: FATTENING_DISEASE_CATEGORIES,
		diagnoseValues: FATTENING_DIAGNOSE_VALUES,
		scoringSystems: { wisconsin: false, faecal: false },
		settingsSection: "fattening",
	},
};

export const PRODUCTION_TYPE_ORDER = [
	PRODUCTION_TYPES.CALF,
	PRODUCTION_TYPES.FATTENING,
];

/**
 * Normalize a raw ProductionType value from Firestore.
 * Unknown or missing values fall back to "calf" — every animal created
 * before this field existed is a calf.
 * @param {string|undefined|null} value
 * @returns {string} A valid production type key
 */
export function normalizeProductionType(value) {
	if (typeof value === "string" && PRODUCTION_TYPE_CONFIG[value.toLowerCase().trim()]) {
		return value.toLowerCase().trim();
	}
	return DEFAULT_PRODUCTION_TYPE;
}

/**
 * Get the full config for a production type (falls back to calf).
 * @param {string} productionType
 * @returns {Object} Registry entry
 */
export function getProductionTypeConfig(productionType) {
	return PRODUCTION_TYPE_CONFIG[normalizeProductionType(productionType)];
}

/**
 * Get the ordered lifecycle stages for a production type.
 * @param {string} productionType
 * @returns {string[]} Stage values in chronological order
 */
export function getLifecycleStageOrder(productionType) {
	return getProductionTypeConfig(productionType).lifecycleStageOrder;
}
